/**
 * Completed Page
 *
 * Displayed when a document has already been signed or form already submitted.
 */
import { CheckCircle2, Download } from 'lucide-react';
import { Button, Card, CardContent } from '@respectly/ui';
import { PortalLayout } from '../layouts/PortalLayout';

interface CompletedPageProps {
  type?: 'signed' | 'declined' | 'submitted';
  documentName?: string;
  completedAt?: Date | string;
  downloadUrl?: string;
}

export function CompletedPage({
  type = 'signed',
  documentName,
  completedAt,
  downloadUrl,
}: CompletedPageProps) {
  const formattedDate = completedAt
    ? new Date(completedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  const getMessage = () => {
    switch (type) {
      case 'signed':
        return 'This document has already been signed.';
      case 'declined':
        return 'This signature request was declined.';
      case 'submitted':
        return 'This form has already been submitted.';
      default:
        return 'This action has already been completed.';
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'signed':
        return 'Already Signed';
      case 'declined':
        return 'Request Declined';
      case 'submitted':
        return 'Already Submitted';
      default:
        return 'Completed';
    }
  };

  return (
    <PortalLayout>
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div
              className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full ${
                type === 'declined' ? 'bg-red-100' : 'bg-green-100'
              }`}
            >
              <CheckCircle2
                className={`h-10 w-10 ${
                  type === 'declined' ? 'text-red-600' : 'text-green-600'
                }`}
              />
            </div>

            <h1 className="text-2xl font-bold text-gray-900">{getTitle()}</h1>

            <p className="mt-3 text-gray-600">{getMessage()}</p>

            {documentName && (
              <p className="mt-2 text-sm font-medium text-gray-700">
                Document: {documentName}
              </p>
            )}

            {formattedDate && (
              <p className="mt-2 text-sm text-gray-500">Completed on {formattedDate}</p>
            )}

            <div className="mt-8 space-y-3">
              {downloadUrl && type === 'signed' && (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => window.open(downloadUrl, '_blank')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download signed document
                </Button>
              )}

              <Button
                variant="outline"
                className="w-full"
                onClick={() => { window.close(); setTimeout(() => { if (!window.closed) window.location.href = '/'; }, 300); }}
              >
                Close this page
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
